import React from 'react';
import { Box, Card, CardActionArea, Paper, Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

/**
 * Building blocks shared by the Analytics and Tracking dashboards, so both
 * pages use one card, one KPI tile and one tile grid at every screen size.
 */

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
const BREAKPOINTS: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];
const GAP_PX = 16;

/** Card padding: a little roomier on large monitors. */
const PANEL_PADDING = { xs: 2, xl: 2.5 } as const;

interface PanelProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Right-aligned header controls (a toggle, a collapse button). */
  action?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/** A dashboard card: outlined surface, h6 title, optional subtitle and header action. */
export const Panel: React.FC<PanelProps> = ({ title, subtitle, action, icon, children, sx }) => (
  <Paper variant="outlined" sx={[{ p: PANEL_PADDING, height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
    {(title || action) && (
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" sx={{ mb: subtitle ? 0.25 : 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {icon}
          {title && (
            <Typography variant="h6" component={typeof title === 'string' ? 'h3' : 'div'}>
              {title}
            </Typography>
          )}
        </Stack>
        {action && <Box sx={{ flexShrink: 0, mt: -0.5, mr: -0.5 }}>{action}</Box>}
      </Stack>
    )}
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '110ch' }}>
        {subtitle}
      </Typography>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Paper>
);

interface KpiTileProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** A change line under the value (arrow + text). */
  delta?: React.ReactNode;
  detail?: React.ReactNode;
  /** Opens the records behind the number; the whole tile becomes the button. */
  onClick?: () => void;
  /** Small status icon beside the label. */
  icon?: React.ReactNode;
  /** Anything under the detail (e.g. a progress bar). */
  footer?: React.ReactNode;
}

/** A headline number: overline label, h4 value, one or two lines of context. */
export const KpiTile: React.FC<KpiTileProps> = ({ label, value, delta, detail, onClick, icon, footer }) => {
  const body = (
    <Box sx={{ p: PANEL_PADDING, width: '100%' }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5, minHeight: 20 }}>
        {icon}
        <Typography variant="overline" color="text.secondary" component="div" sx={{ lineHeight: 1.4 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h4" component="div" className="tabular-nums" sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
      {delta}
      {detail && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {detail}
        </Typography>
      )}
      {footer}
      {onClick && (
        <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 1, fontWeight: 600 }}>
          See the records <ChevronRight sx={{ fontSize: 16 }} />
        </Typography>
      )}
    </Box>
  );
  return (
    <Card variant="outlined" sx={{ height: '100%', width: '100%' }}>
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', alignItems: 'flex-start' }}>
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
};

/**
 * Lays tiles out in rows of `columns` per breakpoint, and stretches the last
 * row's tiles to fill it, so an odd count (7 tiles in rows of 4) never leaves
 * a hole at the end of the grid.
 */
export const TileGrid: React.FC<{ columns: Partial<Record<Breakpoint, number>>; children: React.ReactNode; sx?: SxProps<Theme> }> = ({ columns, children, sx }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  const n = items.length;
  const perBreakpoint: Array<[Breakpoint, number]> = [];
  let current = 1;
  for (const bp of BREAKPOINTS) {
    if (columns[bp] != null) current = columns[bp]!;
    perBreakpoint.push([bp, Math.max(1, Math.min(current, n || 1))]);
  }
  const basis = (index: number) =>
    Object.fromEntries(
      perBreakpoint.map(([bp, cols]) => {
        const rest = n % cols;
        const perRow = rest && index >= n - rest ? rest : cols;
        return [bp, `calc((100% - ${(perRow - 1) * GAP_PX}px) / ${perRow})`];
      }),
    );
  return (
    <Box sx={[{ display: 'flex', flexWrap: 'wrap', gap: `${GAP_PX}px` }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {items.map((child, i) => (
        <Box key={(React.isValidElement(child) && child.key) || i} sx={{ display: 'flex', minWidth: 0, flexGrow: 0, flexShrink: 0, flexBasis: basis(i) }}>
          {child}
        </Box>
      ))}
    </Box>
  );
};
